import { useEffect, useState } from 'react';
import { ArrowDown } from 'lucide-react';
import { CampusAmbassadorForm } from '../components/CampusAmbassadorForm';
import { FoundingCreatorForm } from '../components/careers/FoundingCreatorForm';
import { OpeningsOverview } from '../components/careers/OpeningsOverview';

const SEO = {
  title: 'Careers | Campus Ambassador & Founding Creator | Elevate Careers India',
  description: 'Grow with Elevate Careers India. Join as a Campus Ambassador at your college or as a Founding Creator & Growth Collaborator helping a young brand grow. Open to students across India.',
};

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

const usePageSeo = () => {
  useEffect(() => {
    const prevTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute('content');
    document.title = SEO.title;
    meta?.setAttribute('content', SEO.description);
    return () => { document.title = prevTitle; if (meta && prevDesc) meta.setAttribute('content', prevDesc); };
  }, []);
};

const Hero = () => (
  <section className="relative overflow-hidden bg-[#0b1f4d] py-20 md:py-28">
    <div className="absolute -top-32 right-0 w-[28rem] h-[28rem] rounded-full bg-[#0b1f4d]/25 blur-3xl" aria-hidden="true" />
    <div className="absolute bottom-0 -left-24 w-80 h-80 rounded-full bg-teal-500/15 blur-3xl" aria-hidden="true" />
    <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <p className="text-teal-300 font-medium text-sm tracking-widest uppercase mb-5">Careers — Elevate Careers India</p>
      <h1 data-testid="careers-hero-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-4xl">
        Grow With <span className="text-teal-300">Elevate Careers India</span>
      </h1>
      <p className="mt-5 text-base md:text-lg text-slate-200 font-medium max-w-3xl">
        Two ways to build your experience, your network and the next generation of careers.
      </p>
      <p className="mt-4 text-slate-400 leading-relaxed max-w-3xl">
        Whether you want to lead on your campus or create content that helps a young brand grow, there's a place for you here. No resume needed to get started.
      </p>
      <button onClick={() => scrollTo('openings')} data-testid="careers-view-openings-btn"
        className="mt-8 inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-[#0b1f4d] font-semibold px-7 py-4 rounded-full transition-colors">
        View Open Opportunities <ArrowDown className="w-4 h-4" />
      </button>
    </div>
  </section>
);

const CreatorFormPanel = () => (
  <section id="founding-creator-form" className="bg-slate-50 py-16 md:py-20 scroll-mt-20" data-testid="fc-form-panel">
    <div className="max-w-3xl mx-auto px-4 sm:px-6">
      <div className="bg-white rounded-3xl border border-slate-200 shadow-lg p-6 md:p-10">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Apply: Founding Creator &amp; Growth Collaborator</h2>
        <p className="mt-2 mb-8 text-slate-600">Takes about two minutes. No resume needed.</p>
        <FoundingCreatorForm />
      </div>
    </div>
  </section>
);

export const Careers = () => {
  usePageSeo();
  const [applyFor, setApplyFor] = useState(null);
  const apply = (id) => {
    setApplyFor(id);
    setTimeout(() => scrollTo(id === 'campus-ambassador' ? 'apply' : 'founding-creator-form'), 50);
  };
  return (
    <div data-testid="careers-page" className="min-h-screen bg-white">
      <Hero />
      <OpeningsOverview active={applyFor} onApply={apply} />
      {applyFor === 'campus-ambassador' && <CampusAmbassadorForm />}
      {applyFor === 'founding-creator' && <CreatorFormPanel />}
    </div>
  );
};
